import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { BrandLogo, HeaderMark } from './Brand';

/**
 * Шапка экрана — одна на все экраны приложения, вкладки и вложенные (решение владельца 18.09.2026).
 *
 * Три зоны: кнопка слева («назад», «закрыть»), центр, кнопка справа (колокольчик, «добавить»).
 * В центре знак TT и название экрана; на главной вместо слова «Главная» — логотип целиком, знак и
 * TWINTURBO (`brand`). Отступы и кегль — `.topbar` прототипа: `padding: 10px 16px 14px`, заголовок
 * 25/29/800, −0.01em.
 *
 * ЦЕНТР СТОИТ НА ОСИ ЭКРАНА ВСЕГДА. Боковые колонки — сетка ровно по 36 px, с кнопкой и без, и
 * пустая колонка не отдаёт ширину заголовку. Раньше отдавала (`shrink-[999]`, чтобы «Вы будете на
 * месте?» не обрывался многоточием), и длинное название уезжало вправо ровно на ширину
 * отданного, — это и было «заголовок то слева, то посередине, то справа». Нехватку места теперь
 * решает сам заголовок: вторая строка, а если и так не влезает — меньший кегль, см. `useFitTitle`.
 *
 * Прототип этого правила не знал: у «Главной» заголовок был первым ребёнком ряда с
 * `space-between` и стоял слева, у «Истории» и «Профиля» — единственным и тоже слева, у
 * вложенных — по центру. Веб, Android и iOS прочитали это тремя разными способами.
 *
 * Горизонтальное поле 16 px, а у содержимого экрана 18 — не опечатка: круглые кнопки стоят ближе
 * к краю, чем текст, и мобильные клиенты сохранили ту же разницу.
 */
export function TopBar({
  title,
  leading,
  trailing,
  brand = false,
}: {
  title: string;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Логотип вместо названия — только у главной. `title` остаётся именем заголовка для диктора. */
  brand?: boolean;
}) {
  return (
    <div className="grid shrink-0 grid-cols-[36px_minmax(0,1fr)_36px] items-center gap-2 px-4 pb-3.5 pt-2.5">
      <Slot>{leading}</Slot>
      <div className="flex min-w-0 items-center justify-center gap-2.5">
        {brand ? (
          <h1 className="flex">
            <BrandLogo />
            <span className="sr-only">{title}</span>
          </h1>
        ) : (
          <>
            <HeaderMark />
            <Title text={title} />
          </>
        )}
      </div>
      <Slot>{trailing}</Slot>
    </div>
  );
}

/**
 * Название экрана рядом со знаком.
 *
 * Выравнивание влево внутри своей коробки, а не по центру: пара «знак + название» уже стоит
 * по центру шапки, и вторая строка, отцентрованная отдельно, отлипала бы от знака.
 *
 * Две строки; не влезает — `useFitTitle` уменьшает кегль, а многоточия нет вовсе. Ни
 * `hyphens-auto`, ни `break-words` здесь нет намеренно: они рвали слово
 * («Восстановлени-е» на 320 CSS-px), а владелец выбрал макет, где длинное слово уменьшается.
 * Они же прятали бы нехватку места от замера — слово переносилось бы, а не выпирало.
 */
function Title({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  useFitTitle(ref, text);
  return (
    <h1
      ref={ref}
      className="min-w-0 text-balance text-left text-[25px] font-extrabold leading-[1.16] tracking-[-0.01em] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [overflow:hidden]"
    >
      {text}
    </h1>
  );
}

/** Ниже этой доли от кегля по макету заголовок не уменьшается — дальше уже перенос посреди слова. */
const TITLE_MIN_SCALE = 0.68;

/**
 * Уменьшает кегль заголовка, пока тот не влезет: слово — в ширину, весь текст — в две строки.
 *
 * Мерить приходится в браузере: сколько места достаётся заголовку, зависит от ширины экрана,
 * минимального размера шрифта в настройках браузера и самого названия (документы приходят с
 * сервера). На 320 CSS-px рядом со знаком и кнопкой «назад» остаётся 170 px, а «Восстановление»
 * кеглем 25 — это 200.
 *
 * Шаг — 1 px от кегля по макету вниз, не дальше `TITLE_MIN_SCALE`. Если не влезло и там (название
 * документа «Согласие на обработку персональных данных» на 320 CSS-px — четыре строки, крупный
 * минимальный шрифт браузера), последний довод — столько строк, сколько нужно, и перенос посреди
 * слова: высокая шапка хуже меньшего кегля, но лучше обрезанного названия. Следит за
 * шириной центральной колонки, а не самого заголовка: его ширина меняется от подгонки, и
 * наблюдение за ним зациклилось бы.
 *
 * Последний шаг — ширина коробки по самой длинной строке. Перенесённый текст занимает коробку во
 * всю доступную ширину, как бы коротки ни были его строки, и пара «знак + название» переставала
 * быть по центру: «Вы будете / на месте?» уезжал к левому краю колонки (замер Chrome 18.09.2026).
 * CSS так ужать коробку не умеет, поэтому ширина строк меряется `Range` и ставится явно.
 */
function useFitTitle(ref: RefObject<HTMLHeadingElement | null>, text: string) {
  useLayoutEffect(() => {
    const node = ref.current;
    const column = node?.parentElement;
    // jsdom (тесты) не знает ни ResizeObserver, ни размеров `Range`: вёрстки там нет, мерить нечего.
    if (!node || !column || typeof ResizeObserver === 'undefined') return;

    const overflows = () =>
      node.scrollWidth > node.clientWidth + 0.5 || node.scrollHeight > node.clientHeight + 0.5;

    const fit = () => {
      node.style.fontSize = '';
      node.style.overflowWrap = '';
      node.style.width = '';
      node.style.webkitLineClamp = '';
      const base = parseFloat(getComputedStyle(node).fontSize);
      if (!Number.isFinite(base)) return;
      let size = base;
      while (overflows() && size - 1 >= base * TITLE_MIN_SCALE) {
        size -= 1;
        node.style.fontSize = `${size}px`;
      }
      if (overflows()) {
        node.style.webkitLineClamp = 'none';
        node.style.overflowWrap = 'anywhere';
      }

      const range = document.createRange();
      range.selectNodeContents(node);
      const linesWidth = range.getBoundingClientRect().width;
      // +1: дробная ширина, округлённая вниз, перенесла бы последнее слово на лишнюю строку.
      if (linesWidth > 0 && linesWidth < node.clientWidth) {
        node.style.width = `${Math.ceil(linesWidth) + 1}px`;
      }
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(column);
    return () => observer.disconnect();
  }, [ref, text]);
}

/**
 * Колонка под круглую кнопку — 36 px, и пустая тоже. Пустая ширину не отдаёт: см. `TopBar`.
 */
function Slot({ children }: { children?: ReactNode }) {
  return <div className="flex justify-center">{children}</div>;
}

/**
 * A round 34px control. The circle stays 34 — that is the drawn size in every client — but the
 * hit area is pushed out to 44px with an invisible pseudo-element, because growing the circle
 * instead would change the design and shrinking the target below 44 fails the touch guideline
 * (docs/22 §7.2).
 *
 * `label` is required, not optional: an icon-only control without an accessible name is a
 * definition-of-done failure in this design system, and making the prop optional is how that
 * keeps happening.
 */
export function IconButton({
  label,
  onClick,
  children,
  tone = 'neutral',
  type = 'button',
  disabled,
}: {
  label: string;
  onClick?: () => void;
  children: ReactNode;
  tone?: 'neutral' | 'danger';
  type?: 'button' | 'submit';
  disabled?: boolean;
}) {
  const tones = {
    neutral: 'bg-surface-alt text-ink',
    danger: 'bg-accent-soft text-accent-soft-ink',
  } as const;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={
        'relative grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full ' +
        'transition-transform duration-[120ms] active:scale-[0.94] disabled:opacity-55 ' +
        "before:absolute before:-inset-[5px] before:content-[''] " +
        tones[tone]
      }
    >
      {children}
    </button>
  );
}
